/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Sep 25 2024
 **/

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Column, Row, Tab, TabItem, Typography, useTab } from "@/frameworks/designStuff/components";
import { useChatRoomActions, useSpaceChannelState } from "@/frameworks/core/chat";
import { useFullSizeChatManager, useRoomViewActions } from "@/frameworks/core/hooks";
import { Properties } from "@/frameworks/libraries/properties";

import { FullSizeSpaceNavWrap } from "./private/emotion";
import SpaceChatTab from "./private/SpaceChatTab";

export default function FullSizeOpenWorldChatContent() {
	const { currentId, handleSelectTab } = useTab();
	const queryClient = useQueryClient();
	const publicOpenWorldData = queryClient.getQueryData<
		Map<string, WebServices.Space.InqueryPublicOpenWorldInfoList.Data[number]>
	>(["getPublicOpenWorldChatList"]);
	const tabContainerRef = useRef<HTMLDivElement>(null);
	const spaceInfo = useSpaceChannelState(currentId);
	const chatRoomActions = useChatRoomActions();
	const channelData = spaceInfo?.rooms.at(0);
	const [, setCurrentID] = useState(currentId);
	const { updateRoomView } = useRoomViewActions();
	const { fullSizeChatActions } = useFullSizeChatManager();

	useEffect(() => {
		setCurrentID((prev) => {
			if (prev !== currentId) {
				// 탭 변경시 기존 탭 룸은 roomView false 처리 새로운 roomView는 true 처리
				[
					{
						type: "prevCurrentID",
						id: publicOpenWorldData?.get(prev)?.generalRoomId
					},
					{
						type: "currentID",
						id: publicOpenWorldData?.get(currentId)?.generalRoomId
					}
				].forEach((item) => {
					updateRoomView({
						roomId: item.id as string,
						deviceKey: Properties.browserTabID,
						view: item.type === "currentID" || false
					});

					// 탭 이동시 현재 fullsizeChat의 데이터를 변경 시켜주기 위함.
					fullSizeChatActions.set({
						channelID: publicOpenWorldData?.get(currentId)?.generalRoomId,
						spaceID: currentId
					});
				});
			}
			return currentId;
		});
	}, [currentId]);

	if (!publicOpenWorldData) return null;

	return (
		<>
			<FullSizeSpaceNavWrap>
				<div css={{ overflow: "hidden" }}>
					<Tab id="full-space-chat" ref={tabContainerRef} style={{ padding: 0, borderBottom: "none" }}>
						{[...publicOpenWorldData.values()].map((openworld) => {
							return (
								<TabItem
									id={openworld.spaceId}
									key={openworld.spaceId}
									onClick={async () => {
										await chatRoomActions.spaceChat.openworld.join(
											openworld.spaceId,
											openworld.spaceName
										);
										handleSelectTab(openworld.spaceId);
									}}
								>
									<Row horizonAlign="center" verticalAlign="center" gap={4}>
										<Typography typoSize="Title3">{openworld.spaceName}</Typography>
									</Row>
								</TabItem>
							);
						})}
					</Tab>
				</div>
			</FullSizeSpaceNavWrap>
			{channelData && channelData.roomJoined && (
				<Column css={{ width: "100%", height: "100%", overflow: "hidden" }}>
					<SpaceChatTab
						channelData={channelData}
						key={channelData.roomId}
						spaceID={currentId}
						roomID={channelData.roomId}
					/>
				</Column>
			)}
		</>
	);
}
